import type { ConsentPublicationReference, PrivacyPublication } from './types';

export interface LoadedPublication { publication: PrivacyPublication; online: boolean }

declare global {
  interface Window {
    /** La stessa promise collega i due bundle e fissa una sola release per pagina. */
    __emergePrivacyPublications?: Record<string, Promise<LoadedPublication>>;
    __emergePrivacyReleaseIds?: Record<string, Promise<string>>;
  }
}

export function publicationReference(publication: PrivacyPublication): ConsentPublicationReference {
  const { site_id, release_id, content_hash, consent_scope_hash, lang } = publication;
  return { site_id, release_id, content_hash, consent_scope_hash, lang };
}

export function matchesPublication(value: unknown, publication: PrivacyPublication): boolean {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return ['release_id', 'content_hash', 'consent_scope_hash', 'lang'].every(
    (key) => candidate[key] === publication[key as keyof PrivacyPublication],
  );
}

export function publicationIsCurrent(publication: PrivacyPublication, now = Date.now()): boolean {
  return publication.valid_until === null || Date.parse(publication.valid_until) > now;
}

function validatePublication(value: unknown, siteId: string, lang: string, releaseId?: string): PrivacyPublication {
  if (!value || typeof value !== 'object') throw new Error('publication_invalid');
  const p = value as PrivacyPublication;
  if (p.site_id !== siteId || p.lang !== lang || (releaseId && p.release_id !== releaseId)
      || typeof p.release_id !== 'string' || !p.release_id
      || typeof p.content_hash !== 'string' || !/^[a-f0-9]{64}$/i.test(p.content_hash)
      || typeof p.consent_scope_hash !== 'string' || !/^[a-f0-9]{64}$/i.test(p.consent_scope_hash)
      || typeof p.banner_version !== 'string' || !p.banner_version
      || (p.valid_until !== null && typeof p.valid_until !== 'string')
      || !publicationIsCurrent(p)) throw new Error('publication_identity_or_expiry');
  if (!p.banner || typeof p.banner.enabled !== 'boolean' || p.banner.lang !== lang
      || !Array.isArray(p.banner.categories)
      || p.banner.categories.some((category) => !['analytics', 'marketing', 'functional'].includes(category))
      || !['privacy_url', 'cookie_url', 'accent'].every((key) => typeof p.banner[key as keyof typeof p.banner] === 'string')) {
    throw new Error('publication_banner_invalid');
  }
  for (const page of ['privacy', 'cookie'] as const) {
    const document = p.documents?.[page];
    if (!document || !Array.isArray(document.sections)
        || !document.sections.every((s) => s && typeof s.slug === 'string' && typeof s.html === 'string')
        || (document.updated_at !== null && typeof document.updated_at !== 'string')) {
      throw new Error('publication_document_invalid');
    }
  }
  return p;
}

/** Fallback alla sola fotografia completa già ammessa, mai dopo revoca/errore HTTP. */
export async function fetchPublication(siteId: string, base: string, lang: string, releaseId?: string): Promise<LoadedPublication> {
  const cacheKey = `emerge-privacy-publication-${siteId}-${lang}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2000);
  try {
    const query = new URLSearchParams({ lang, ...(releaseId ? { release_id: releaseId } : {}) });
    let response: Response;
    try {
      response = await fetch(`${base}/api/policy/${encodeURIComponent(siteId)}/publication?${query}`, { signal: controller.signal, cache: 'no-store' });
    } catch (error) {
      try {
        const cache = JSON.parse(localStorage.getItem(cacheKey) ?? 'null');
        if (!cache || !Number.isFinite(Date.parse(cache.received_at)) || Date.parse(cache.received_at) > Date.now()) throw error;
        return { publication: validatePublication(cache.publication, siteId, lang, releaseId), online: false };
      } catch { throw error; }
    }
    if (!response.ok) throw new Error(`publication_http_${response.status}`);
    const publication = validatePublication(await response.json(), siteId, lang, releaseId);
    // Il recupero di una release storica serve solo a verificarla e non sostituisce
    // la copia corrente usata per il fallback della pagina successiva.
    if (!releaseId) {
      try { localStorage.setItem(cacheKey, JSON.stringify({ received_at: new Date().toISOString(), publication })); } catch { /* storage opzionale */ }
    }
    return { publication, online: true };
  } finally {
    clearTimeout(timer);
  }
}

export function loadPagePublication(siteId: string, base: string, lang: string, releaseId?: string): Promise<LoadedPublication> {
  const siteKey = `${base}|${siteId}`;
  const key = `${siteKey}|${lang}`;
  const publications = window.__emergePrivacyPublications ??= {};
  const releaseIds = window.__emergePrivacyReleaseIds ??= {};
  let loaded = publications[key];
  if (!loaded) {
    const pinnedId = releaseIds[siteKey];
    loaded = publications[key] = pinnedId
      ? pinnedId.then((id) => fetchPublication(siteId, base, lang, id))
      : fetchPublication(siteId, base, lang, releaseId);
    if (!pinnedId) {
      releaseIds[siteKey] = loaded.then((result) => result.publication.release_id);
      void releaseIds[siteKey].catch(() => undefined);
    }
  }
  return loaded.then((result) => {
    if (releaseId && result.publication.release_id !== releaseId) throw new Error('publication_page_mismatch');
    if (!publicationIsCurrent(result.publication)) throw new Error('publication_expired');
    return result;
  });
}
